import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import PaperIcon from "@/assets/images/paper-blue.png";
import { ReactComponent as PlusSvg } from "@/assets/images/plus.svg";
import { ReactComponent as MinusSvg } from "@/assets/images/minus-background.svg";
import CommonImg from "@/components/Common/Img";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { cn } from "@/libs/utils";
import ControlledTextarea from "@/components/Common/form/ControlledTextarea";
import { Form } from "@/components/ui/form";
import ApprovalAccordionContent from "./ApprovalAccordionContent";
import { useEstimateInfo } from "../hooks/useScenarioStore";
import { useCreateScenarioApprovals } from "@/libs/react-query/hooks/useScenarioApprovalsQuery";
import { useParams } from "react-router-dom";
import { showToast } from "@/utils/toastUtils";
import { Scenario } from "@/types/scenario";

const formSchema = z.object({
  description: z.string().optional(),
  approvalSteps: z
    .array(
      z.object({
        approvers: z
          .array(z.string().min(1))
          .min(1, "각 단계에는 최소 1명의 담당자가 필요합니다.")
          .default([]),
        references: z.array(z.string().min(1)).optional(),
      })
    )
    .min(1, "최소 1단계 이상 필요합니다.")
    .max(5, "최대 5단계까지만 설정 가능합니다."),
});

export type ScenarioApprovalFormSchema = z.infer<typeof formSchema>;

type Props = {
  scenario: Scenario;
};

export default function ApprovalScenarioDialog({ scenario }: Props) {
  const { group_id = "" } = useParams();

  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(1);

  const estimateInfo = useEstimateInfo();

  const { mutate: createScenarioSpproval } = useCreateScenarioApprovals();

  const form = useForm<ScenarioApprovalFormSchema>({
    resolver: zodResolver(formSchema),
  });
  const {
    setValue,
    getValues,
    handleSubmit,
    formState: { isValid, isDirty },
  } = form;

  const onSubmit = (data: ScenarioApprovalFormSchema) => {
    if (!estimateInfo || !scenario) return;

    const { project_id, scenario_id, project_type, title } = scenario;

    createScenarioSpproval(
      {
        scenario_id,
        project_id,
        project_type,
        project_group_id: group_id,
        title: title,
        description: data.description ?? "",
        stages: data.approvalSteps.map((step) => ({
          to_list: step.approvers,
          cc_list: step.references ?? [],
        })),
      },
      {
        onSuccess: (data) => {
          showToast({
            message: "시나리오가 승인 요청되었습니다.",
            type: "success",
          });
          form.reset();
          setOpen(false);
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      modal
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSteps(1);
          form.reset();
        }
      }}
    >
      <DialogTrigger>
        <div className="text-blue-ThermoFisher  px-5 py-2 flex gap-1 hover:bg-gray-E">
          <CommonImg src={PaperIcon} alt="C" className="w-3 h-3" />
          시나리오 승인
        </div>
      </DialogTrigger>

      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <DialogHeader className="text-center text-lg font-semibold">
              <DialogTitle>시나리오 승인</DialogTitle>
              <DialogDescription />
            </DialogHeader>
            <Accordion type="single" collapsible className="w-full">
              {Array.from({ length: steps }, (_, index) => {
                const approvalSteps = getValues("approvalSteps");
                const currentStep = approvalSteps?.[index];
                const approversExist = currentStep?.approvers?.length > 0;
                const referencesExist =
                  (currentStep?.references ?? []).length > 0;
                const canRemove =
                  steps > 1 && !approversExist && !referencesExist;

                return (
                  <ApprovalStepAccordion
                    key={`accordion-${index}`}
                    index={index}
                    canRemove={canRemove}
                    onRemove={() => {
                      const targetStep = approvalSteps.filter(
                        (_, i) => i !== index
                      );
                      setValue("approvalSteps", targetStep, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                      setSteps((prev) => prev - 1);
                    }}
                  />
                );
              })}
            </Accordion>

            <button
              type="button"
              className="self-center"
              disabled={steps >= 5}
              onClick={() => setSteps((prev) => prev + 1)}
            >
              <PlusSvg
                className={cn("size-6 text-dark-brown", {
                  "text-gray-C": steps >= 5,
                })}
              />
            </button>

            <ControlledTextarea
              name="description"
              noLabel
              placeholder="요청 사유를 입력하세요"
              className="h-60 resize-none"
            />
            <DialogFooter>
              <Button
                size="md"
                type="submit"
                className="w-40 self-center bg-dark-red text-sm text-white font-semibold rounded-md hover:bg-dark-red/80"
                disabled={!isValid || !isDirty}
              >
                설정 완료
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

function ApprovalStepAccordion({
  index,
  canRemove,
  onRemove,
}: {
  index: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  return (
    <AccordionItem value={`step-${index}`}>
      <div className="flex items-center gap-2">
        <AccordionTrigger>{index + 1}단계</AccordionTrigger>
        {canRemove && (
          <MinusSvg className="w-4 h-4 cursor-pointer" onClick={onRemove} />
        )}
      </div>

      <AccordionContent className="flex flex-col gap-1">
        <ApprovalAccordionContent index={index} variant="approvers" />
        <ApprovalAccordionContent index={index} variant="references" />
      </AccordionContent>
    </AccordionItem>
  );
}
